import { Flex, Text } from "styles/utility-components";

import {
  MCP_SERVER_DESCRIPTION,
  MCP_SERVER_NAME,
  MCP_SERVER_URL,
} from "components/integrations/mcpProviders";
import { CopyableField } from "components/ui/copyable_field/CopyableField";
import { authBaseUrl } from "lib/auth/authClient";
import { MCP_CLIENT_SCOPES } from "lib/hooks/api/auth/mcp/schemas";

export type ConnectionField = {
  label: string;
  value: string;
  // For values with no natural break points, like the ID and secret.
  wrapValue?: boolean;
};

// Refreshing a token goes to the same endpoint as the first exchange.
const TOKEN_URL = `${authBaseUrl}/oauth2/token`;

// Everything a manually configured OAuth form asks for besides the credential, labelled as Copilot
// Studio labels it. The same for every tool, so nothing here needs a provider.
export const SERVER_FIELDS: ConnectionField[] = [
  { label: "Server name", value: MCP_SERVER_NAME },
  { label: "Server description", value: MCP_SERVER_DESCRIPTION },
  { label: "Server URL", value: MCP_SERVER_URL },
];

export const OAUTH_ENDPOINT_FIELDS: ConnectionField[] = [
  { label: "Authorization URL", value: `${authBaseUrl}/oauth2/authorize` },
  { label: "Token URL", value: TOKEN_URL },
  { label: "Refresh token URL", value: TOKEN_URL },
  { label: "Scopes", value: MCP_CLIENT_SCOPES.join(" ") },
];

type ConnectionFieldsProps = {
  fields: ConnectionField[];
  /**
   * Sizes each field to its value instead of the container. For the page, where a full-width field
   * for a one-word value looks like a stretched input; the dialog's narrow column keeps them flush.
   */
  fitContent?: boolean;
};

/** Labelled values with a copy button each, for pasting into an AI tool's form. */
export function ConnectionFields({ fields, fitContent = false }: ConnectionFieldsProps) {
  return (
    <Flex column gap={12} alignItems={fitContent ? "flex-start" : undefined}>
      {fields.map(({ label, value, wrapValue }) => (
        <Flex column gap={6} key={label}>
          <Text description>{label}</Text>
          <CopyableField
            value={value}
            toastMessage={`${label} copied to clipboard`}
            wrapValue={wrapValue}
          />
        </Flex>
      ))}
    </Flex>
  );
}

/**
 * The details that are the same for every credential: the server and its OAuth endpoints. Shown in
 * the setup guidance as well as the credential dialog, so they can be found again once the dialog
 * has closed — only the secret has to be copied from there.
 */
export function McpConnectionDetails() {
  return <ConnectionFields fields={[...SERVER_FIELDS, ...OAUTH_ENDPOINT_FIELDS]} fitContent />;
}
